import type { IControl, Map as MaplibreMap } from 'maplibre-gl';

export type PanDirection = 'left' | 'up' | 'down' | 'right';

/** The buttons in their order, named by what pressing them shows next. */
export const PAN_BUTTONS: readonly { direction: PanDirection; label: string }[] = [
  { direction: 'left', label: 'Weiter links zeigen' },
  { direction: 'up', label: 'Weiter oben zeigen' },
  { direction: 'down', label: 'Weiter unten zeigen' },
  { direction: 'right', label: 'Weiter rechts zeigen' },
];

/** how far one press moves the view, as a share of the map's width or height */
const PAN_SHARE = 1 / 3;
/** MapLibre's margin around the controls in a corner of the map (px) */
const CONTROL_MARGIN = 10;
/** the room (px) between the pan buttons and the lowest place of a framed answer: its pin and the
 *  name beside it stand clear of the buttons */
const CLEARANCE = 22;

/** The bottom padding (px) an answer is framed with when the map's buttons are `ctrl` px: the strip
 *  of the pan buttons, and the room above it. */
export function framePaddingBottom(ctrl: number): number {
  return CONTROL_MARGIN + ctrl + CLEARANCE;
}

/** The offset (px, as `Map.panBy` takes it) that moves the view of a `width` × `height` map
 *  towards `direction`. */
export function panOffset(direction: PanDirection, width: number, height: number): [number, number] {
  switch (direction) {
    case 'left': return [-width * PAN_SHARE, 0];
    case 'right': return [width * PAN_SHARE, 0];
    case 'up': return [0, -height * PAN_SHARE];
    case 'down': return [0, height * PAN_SHARE];
  }
}

const NAME = 'Ausschnitt verschieben';

// a chevron pointing up; each button turns it towards its side
const CHEVRON = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 15l6-6 6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// arrows to all four sides, on the button that opens the pan buttons
const MOVE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 3v18M3 12h18M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Four buttons that move the map without dragging it (WCAG 2.5.7 Dragging Movements): a mouse drags
 * the map, a finger drags it after a tap or with two fingers — each of these is also a single press
 * here. A row in the bottom-left corner, inside the strip `AnswerMap.show` keeps free when it frames
 * an answer (`framePaddingBottom`). On a narrow map (styles.css) the row would cover a good part of
 * the answer: one button „Ausschnitt verschieben“ stands there instead and opens the row above it
 * (`aria-expanded`); pressing an arrow stays a single press.
 */
export class PanControl implements IControl {
  private control: HTMLElement | null = null;

  onAdd(map: MaplibreMap): HTMLElement {
    const control = (this.control = document.createElement('div'));
    control.className = 'maplibregl-ctrl pan-control';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pan-toggle';
    toggle.setAttribute('aria-label', NAME);
    toggle.setAttribute('aria-expanded', 'false');
    toggle.title = NAME;
    toggle.innerHTML = MOVE;
    toggle.addEventListener('click', () => toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true')));
    const opener = document.createElement('div');
    opener.className = 'maplibregl-ctrl-group pan-opener';
    opener.appendChild(toggle);
    const group = document.createElement('div');
    group.className = 'maplibregl-ctrl-group pan-arrows';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', NAME);
    for (const { direction, label } of PAN_BUTTONS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pan-${direction}`;
      button.setAttribute('aria-label', label);
      button.title = label;
      button.innerHTML = CHEVRON;
      button.addEventListener('click', () => {
        const canvas = map.getCanvas();
        map.panBy(panOffset(direction, canvas.clientWidth, canvas.clientHeight), { duration: 300 });
      });
      group.appendChild(button);
    }
    // the opener first: the focus meets it before the arrows it opens
    control.append(opener, group);
    return control;
  }

  onRemove(): void {
    this.control?.remove();
    this.control = null;
  }
}
